
import {
    defineComponent, nextTick, Ref, ref, watch, onBeforeUnmount
} from 'vue';
import '@/components/view/common/dashboard/common.less';
import itemTitle from '@/components/view/common/dashboard/ItemTitle.vue';
import cardIcon from '@/components/view/common/dashboard/CardIcon.vue';
import entryRecord from '@/components/view/common/dashboard/EntryRecord.vue';
import secondaryNav from '@/components/common/secondary-nav/index.vue';
import HttpRequest from '@/util/axios.config';
import EnlargeImage from '@/components/common/enlarge-image/index.vue';
import router from '@/router';
import { community } from '@/data';
import {
    infoData, reminderData, countsCharts, countsNav
} from './office';
import {
    ReminderType, OfficeInfoType, OfficeKeyType,
    DoorType, RecordType, OfficeCountsChartType,
    TimesChartType,
    OfficeData, AppType
} from './all.type';
import { timesCharts, TimesNav, addColor } from './util';

export default defineComponent({
    components: {
        itemTitle,
        cardIcon,
        secondaryNav,
        entryRecord,
        EnlargeImage
    },
    setup() {
        const interval: Ref<any> = ref(0);
        // 离开当前页面销毁
        onBeforeUnmount(() => {
            clearInterval(interval.value);
        });
        // 卡片数据reminder/info
        const reminderNum: Ref<ReminderType> = ref({
            Offline: 0,
            Inactivated: 0,
            Expiring: 0,
            Expired: 0
        });
        const infoNum: Ref<OfficeInfoType> = ref({
            Department: 0,
            Staffs: 0,
            Personnels: 0,
            DeviceAll: 0
        });
        // 统计图数据counts/times
        const counts: Ref<OfficeKeyType> = ref({
            TempKey: [],
            Staff: [],
            Personnel: []
        });
        const times: Ref<DoorType> = ref({
            Day: [],
            Month: []
        });

        const logImageMap = new Map<string, string>();

        // 每5秒获取Entry Record数据
        const recordData: Ref<Array<RecordType>> = ref([]);
        const getRecord = () => {
            if (community.enableShowLog.value) {
                HttpRequest.get('v4/web/office/log/pm/getLatestfifteenDoorLog', [{}, false], (res: {
                    data: {
                        list: Array<{
                            id: string;
                            mac: string;
                            deviceName: string;
                            initiator: string;
                            captureType: string;
                            picUrl: string;
                            smallPicUrl: string;
                            captureTime: string;}>;
                    };}) => {
                    const tempData: Array<RecordType> = [];

                    /**
                     * 缓存图片地址与图片完整 url 的映射，地址相同时无视地址后续参数，取缓存的 url
                     * 避免因防盗链导致的无法缓存
                     */
                    res.data.list.forEach((item) => {
                        const tempItem: any = {
                            CaptureTime: item.captureTime,
                            Initiator: (item.initiator === 'visitor' || item.initiator === 'Unknown') ? WordList.visitor : item.initiator,
                            Location: item.deviceName,
                            PicUrl: item.picUrl,
                            CachedPicUrl: ''
                        };
                        const url = item.picUrl.split('?')[0];
                        if (logImageMap.has(url)) {
                            tempItem.CachedPicUrl = logImageMap.get(url) as string;
                        } else {
                            tempItem.CachedPicUrl = item.picUrl;
                            logImageMap.set(url, item.picUrl);
                        }
                        tempData.push(tempItem);
                    });

                    recordData.value = tempData;
                });
            }
        };

        // 有 log 权限时才会请求接口
        watch(() => community.enableShowLog, () => {
            getRecord();
        }, {
            immediate: true
        });
        interval.value = setInterval(() => {
            getRecord();
        }, 5000);

        // 获取首页面板其他数据
        HttpRequest.get('v3/web/office/project/getAllOfficeData', {}, (res: {
            data: OfficeData;
        }) => {
            const {
                Department, Device, People, Key
            } = res.data;
            reminderNum.value.Offline = Device.Offline;
            infoNum.value.DeviceAll = Device.All;
            infoNum.value.Department = Department;
            Object.keys(People).forEach((val) => {
                const key = val as keyof AppType;
                if (val !== 'Personnels' && val !== 'Staffs') {
                    reminderNum.value[key] = People[key];
                } else {
                    infoNum.value[val] = People[key];
                }
            });
            counts.value = Key;
            nextTick(() => {
                if (document.getElementById('counts-container')) {
                    countsCharts(counts.value.Personnel, 'Personnel');
                }
            });
        });

        HttpRequest.get('v4/web/office/log/pm/getDoorLogStatisticsForOld', {}, (res: {
            data: {
                day: number[];
                month: number[];
            };
        }) => {
            times.value.Day = addColor(res.data.day);
            times.value.Month = addColor(res.data.month);
            nextTick(() => {
                if (document.getElementById('counts-container')) {
                    if (community.enableShowLog.value) {
                        timesCharts(times.value.Day);
                    }
                }
            });
        });

        // 监听counts标签，切换charts统计图
        const activeCountsItem: Ref<OfficeCountsChartType> = ref('Personnel');
        watch(activeCountsItem, () => {
            countsCharts(counts.value[activeCountsItem.value], activeCountsItem.value);
        });
        const changeCountsChart = (key: OfficeCountsChartType) => {
            activeCountsItem.value = key;
        };

        // 监听times标签，切换charts统计图
        const activeTimesItem: Ref<TimesChartType> = ref('Day');
        watch(activeTimesItem, () => {
            timesCharts(times.value[activeTimesItem.value]);
        });
        const changeTimesChart = (key: TimesChartType) => {
            activeTimesItem.value = key;
        };

        // 图片放大的组件
        const isShowEnlargeImage = ref(false);
        const picAddress = ref('');
        const enlargeImg = (img: string) => {
            picAddress.value = img;
            isShowEnlargeImage.value = true;
        };

        // 跳转制定页面
        const goPath = (path: string) => {
            router.push(path);
        };

        return {
            countsNav,
            TimesNav,
            changeCountsChart,
            changeTimesChart,
            activeCountsItem,
            activeTimesItem,
            reminderData,
            reminderNum,
            infoData,
            infoNum,
            recordData,
            enlargeImg,
            isShowEnlargeImage,
            picAddress,
            goPath,
            community
        };
    }
});
